/**
 * ActivityJourney — collapsible, stateful vertical timeline for Karmana activities.
 * 5 steps: Register → Bring Friends → Participate → Share Experience → Inspire Others
 *
 * Phase 3: QR check-in scanner in Step 3 + new S4/S5 unlock state machine.
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Phone, Share2, Download, Send, X, Image as ImageIcon, Copy, Check,
  QrCode, Loader,
} from 'lucide-react';
import {
  getMyActivityInvites,
  getJourneyProgress,
  markParticipation,
  qrCheckin,
  markInspireShared,
  generateInspireImage,
  getInviteCode,
  postActivityComment,
  uploadUserImage,
  updateProfile,
  getMe,
} from '../api/api';
import { useAuth } from '../context/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────
// Default template poster — shown until admin uploads an activity-specific badge
const DEFAULT_INSPIRE_IMAGE_URL = null; // generated per-user; no static placeholder

// Step 5 placeholder images (shown when step is not yet active)
const GENERIC_PLACEHOLDER    = '/inspire/generic_default.png';
const PHOTO_PLACEHOLDER      = '/inspire/photo_default.png';
const CERT_ICON              = '/inspire/certificate_icon.png';

const MAX_MEDIA = 5;

// ── Date/time helpers ──────────────────────────────────────────────────────────
function toDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d;
}
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

/** Returns the event's start datetime. Falls back to 10pm on event date if no start_time. */
function getEventStartDatetime(activity) {
  const dateStr = activity?.date || activity?.date_start;
  if (!dateStr) return null;
  const startTimeStr = activity?.start_time;
  if (startTimeStr) {
    const parts = startTimeStr.split(':');
    const dt = new Date(`${dateStr}T00:00:00`);
    dt.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
    return dt;
  }
  // No start time → activate attendance at 22:00 on event date
  return new Date(`${dateStr}T22:00:00`);
}

/** Returns the event's end datetime (date_end or date + end_time, fallback 23:59:59). */
function getEventEndDatetime(activity) {
  const endDateStr = activity?.date_end || activity?.date || activity?.date_start;
  if (!endDateStr) return null;
  const endTimeStr = activity?.end_time;
  if (endTimeStr) {
    const parts = endTimeStr.split(':');
    const dt = new Date(`${endDateStr}T00:00:00`);
    dt.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
    return dt;
  }
  return new Date(`${endDateStr}T23:59:59`);
}

// ── Step state machine ─────────────────────────────────────────────────────────
function computeStepStates({ alreadyRegistered, inviteCount, journeyProgress, activity }) {
  const now = new Date();
  const eventDate = toDay(activity?.date || activity?.date_start);
  const oneDayBefore = eventDate ? new Date(eventDate.getTime() - 86400000) : null;
  const eventEndTime = getEventEndDatetime(activity);
  const eventStartTime = getEventStartDatetime(activity);

  const attended = journeyProgress?.attended ?? null;
  const experienceShared = journeyProgress?.experience_shared ?? false;
  const inspireShared = journeyProgress?.inspire_shared ?? false;

  // Phase 3: verification info from journey-progress
  const verificationEnabled = journeyProgress?.verification_enabled ?? false;
  const verificationType    = journeyProgress?.verification_type ?? null;
  const verified            = journeyProgress?.verified ?? false;

  const step2Skippable = alreadyRegistered && oneDayBefore && now >= oneDayBefore;
  const step3Active = alreadyRegistered && (inviteCount > 0 || step2Skippable);

  const showContactInfo = !!(step3Active && attended !== true);
  const showAttendanceQuestion = !!step3Active;
  const attendanceClickable = !!(eventStartTime && now >= eventStartTime);

  const s1 = alreadyRegistered ? 'done' : 'current';
  const s2 = !alreadyRegistered ? 'pending'
    : inviteCount > 0 ? 'done'
    : 'current';
  const s3 = !step3Active ? 'pending'
    : attended === true ? 'done'
    : 'current';

  // ── S4: unlocks when attended=Yes ────────────────────────────────────────────
  const s4 = attended !== true ? 'pending'
    : experienceShared ? 'done'
    : 'current';

  // ── S5: no longer gated by S4; unlock depends on verification ────────────────
  let s5Unlocked = false;
  let s5LockedMessage = null;

  if (attended === true) {
    if (!verificationEnabled) {
      // No verification: S5 unlocks with attended=Yes (S4 does NOT gate S5)
      s5Unlocked = true;
    } else if (verificationType === 'qr_code') {
      // QR: S5 unlocks when today >= date_end AND at least 1 verified check-in
      const todayStr = new Date().toISOString().split('T')[0];
      const dateEndStr = activity?.date_end || activity?.date;
      const eventEnded = !!(dateEndStr && todayStr >= dateEndStr);
      s5Unlocked = verified && eventEnded;
      if (verified && !eventEnded) {
        s5LockedMessage = dateEndStr
          ? `Available after the event ends on ${formatDate(dateEndStr)}`
          : 'Available after the event ends';
      }
    } else if (verificationType === 'manual') {
      // Manual: S5 unlocks after organiser verifies
      s5Unlocked = verified;
      if (!verified) {
        s5LockedMessage = 'Awaiting organiser verification';
      }
    }
  }

  const s5 = !s5Unlocked ? 'pending'
    : inspireShared ? 'done'
    : inspireShared ? 'done'
    : 'current';

  const line12 = s1 === 'done';
  const line23 = s2 === 'done' || step2Skippable;
  const line34 = s3 === 'done';
  const line45 = s4 === 'done';

  return {
    s1, s2, s3, s4, s5,
    line12, line23, line34, line45,
    step2Skippable, step3Active,
    showContactInfo, showAttendanceQuestion, attendanceClickable,
    eventDate,
    verificationEnabled, verificationType, verified,
    s5LockedMessage,
  };
}

// ── Dot ────────────────────────────────────────────────────────────────────────
function Dot({ state, primary = true, noTick = false }) {
  const sz = primary ? 16 : 13;
  if (state === 'done') {
    return (
      <div style={{
        width: sz, height: sz, flexShrink: 0, borderRadius: '50%',
        backgroundColor: '#16a34a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!noTick && (
          <svg width={sz - 4} height={sz - 4} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
    );
  }
  if (state === 'current') {
    return (
      <div style={{
        width: sz, height: sz, flexShrink: 0, borderRadius: '50%',
        border: '2px solid #22c55e', background: 'white',
        boxShadow: '0 0 0 3px rgba(34,197,94,0.2), 0 0 8px rgba(34,197,94,0.35)',
      }} />
    );
  }
  return (
    <div style={{ width: sz, height: sz, flexShrink: 0, borderRadius: '50%', background: primary ? '#d1d5db' : '#e5e7eb' }} />
  );
}

// ── Step row ───────────────────────────────────────────────────────────────────
function StepRow({ state, primary = true, noTick = false, title, inlineMeta, children, isFirst, isLast, lineAfter, onTitleClick, rightContent }) {
  const titleColor = state === 'pending' ? '#9ca3af' : '#111827';
  const titleSize  = primary ? 14 : 13;
  const titleWeight = primary ? 700 : 600;

  const titleEl = (
    <div className="flex items-baseline flex-wrap" style={{ gap: 4 }}>
      {onTitleClick ? (
        <button
          onClick={onTitleClick}
          className="text-left hover:opacity-80 transition-opacity"
          data-testid={`journey-step-title-${title.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <span style={{ fontSize: titleSize, fontWeight: titleWeight, color: titleColor, lineHeight: 1.35 }}>
            {title}
          </span>
        </button>
      ) : (
        <span
          style={{ fontSize: titleSize, fontWeight: titleWeight, color: titleColor, lineHeight: 1.35 }}
          data-testid={`journey-step-title-${title.replace(/\s+/g, '-').toLowerCase()}`}
        >
          {title}
        </span>
      )}
      {inlineMeta}
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      {/* Dot + line */}
      <div style={{
        width: 20, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        alignSelf: 'stretch', paddingTop: 1,
      }}>
        <Dot state={state} primary={primary} noTick={noTick} />
        {!isLast && (
          <div style={{
            flex: 1, width: 1, minHeight: 20,
            backgroundColor: lineAfter ? '#16a34a' : '#e5e7eb',
          }} />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: 0, paddingBottom: isLast ? 4 : 20 }}>
        {/* Title row — right content wraps on mobile */}
        <div className="flex items-start justify-between flex-wrap" style={{ gap: 8 }}>
          {titleEl}
          {rightContent && <div className="flex-shrink-0">{rightContent}</div>}
        </div>
        {children && <div style={{ marginTop: 8 }}>{children}</div>}
      </div>
    </div>
  );
}

// ── Inline meta badges ─────────────────────────────────────────────────────────
function YoureHereBadge() {
  return (
    <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full" style={{ whiteSpace: 'nowrap', lineHeight: 1.4 }}>
      You're here
    </span>
  );
}
function DateBadge({ date }) {
  if (!date) return null;
  return (
    <span className="text-[11px] text-gray-400 font-normal" style={{ lineHeight: 1.35, whiteSpace: 'nowrap' }}>
      on {date}
    </span>
  );
}

// ── WhatsApp message helper ───────────────────────────────────────────────────
function buildWhatsAppUrl(activity, shortUrl) {
  const name    = activity?.name || '';
  const date    = formatDate(activity?.date || activity?.date_start);
  const address = activity?.location?.address || '';
  const msg = `Hi, I am going to ${name} happening on ${date}. This is happening at ${address}. Would you like to join me? Register here ${shortUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
}

// ── WhatsApp icon SVG ─────────────────────────────────────────────────────────
function WAIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M11.5 2C6.253 2 2 6.252 2 11.5c0 1.786.483 3.461 1.32 4.902L2 22l5.735-1.303A9.477 9.477 0 0011.5 22c5.247 0 9.5-4.253 9.5-9.5S16.747 2 11.5 2zm0 17.4a7.88 7.88 0 01-4.02-1.098l-.289-.172-2.985.679.715-2.9-.188-.298A7.877 7.877 0 013.6 11.5c0-4.355 3.544-7.9 7.9-7.9s7.9 3.545 7.9 7.9-3.544 7.9-7.9 7.9z"/>
    </svg>
  );
}

function IGIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );
}

// ── Experience form ────────────────────────────────────────────────────────────
function ExperienceForm({ activityId, onPosted }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const { mutate: submitComment, isPending: submitting } = useMutation({
    mutationFn: (data) => postActivityComment(activityId, data),
    onSuccess: () => {
      setText('');
      setMediaUrls([]);
      queryClient.invalidateQueries({ queryKey: ['activity-comments', activityId] });
      queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] });
      onPosted?.();
    },
    onError: (err) => alert(err?.response?.data?.detail || 'Failed to share.'),
  });

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_MEDIA - mediaUrls.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(files.map((f) => uploadUserImage(f)));
      const urls = results.map((r) => r.data?.url || r.data?.file_url || '').filter(Boolean);
      setMediaUrls((prev) => [...prev, ...urls].slice(0, MAX_MEDIA));
    } catch { /* ignore */ } finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!text.trim() && !mediaUrls.length) return; submitComment({ text: text.trim(), media_urls: mediaUrls }); }}>
      <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Share what it was like to participate…"
          rows={2}
          maxLength={1000}
          className="w-full px-3 pt-2.5 bg-transparent text-xs text-gray-800 placeholder-gray-400 resize-none focus:outline-none"
          data-testid="journey-experience-textarea"
        />
        {mediaUrls.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pb-2">
            {mediaUrls.map((url, idx) => (
              <div key={idx} className="relative">
                <img src={url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                <button type="button" onClick={() => setMediaUrls((p) => p.filter((_, i) => i !== idx))}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-gray-700 rounded-full flex items-center justify-center">
                  <X size={8} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between px-3 pb-2.5">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || mediaUrls.length >= MAX_MEDIA}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Add photo">
            <ImageIcon size={14} />
          </button>
          <button type="submit" disabled={submitting || uploading || (!text.trim() && !mediaUrls.length)}
            className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-white text-xs font-semibold rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50"
            data-testid="journey-experience-submit">
            <Send size={10} /> {submitting ? 'Commenting…' : 'Comment'}
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
    </form>
  );
}

// ── QR Scanner Modal ──────────────────────────────────────────────────────────
/**
 * Opens device camera and scans a QR code.
 * On a valid Karmana check-in URL, extracts the token and calls onTokenFound(token).
 * Falls back gracefully when html5-qrcode fails to load.
 */
function QRScannerModal({ onTokenFound, onClose, alreadyCheckedInToday }) {
  const scannerRef      = useRef(null);
  const tokenUsedRef    = useRef(false);      // prevents multiple success-callback fires
  const onTokenFoundRef = useRef(onTokenFound); // stable ref — avoids effect restart on parent re-render
  const [error, setError]       = useState(null);
  const [scanning, setScanning] = useState(false);

  // Keep the ref up-to-date on every render (no effect needed — synchronous assignment)
  onTokenFoundRef.current = onTokenFound;

  useEffect(() => {
    if (alreadyCheckedInToday) return;

    let html5QrCode = null;
    let mounted = true;

    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted) return;
        html5QrCode = new Html5Qrcode('qr-scanner-reader');
        scannerRef.current = html5QrCode;
        if (!mounted) return;
        setScanning(true);

        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          async (decodedText) => {
            // Guard: only process the first valid scan
            if (tokenUsedRef.current || !mounted) return;

            try {
              const url   = new URL(decodedText);
              const token = url.searchParams.get('token');
              if (token) {
                tokenUsedRef.current = true;
                // Stop the camera BEFORE handing off — prevents re-fire after unmount
                try {
                  await scannerRef.current?.stop();
                  scannerRef.current = null;
                } catch { /* ignore */ }
                // Use ref to call the latest callback — avoids stale closure
                if (mounted) onTokenFoundRef.current(token);
              } else if (mounted) {
                setError('Invalid QR code. Make sure you scan the event check-in code.');
              }
            } catch {
              if (mounted) setError('Invalid QR code. Make sure you scan the event check-in code.');
            }
          },
          () => {} // frame with no QR — no-op
        );
      } catch {
        if (mounted) setError('Camera access failed. Please allow camera permission and try again.');
      }
    })();

    return () => {
      mounted = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [alreadyCheckedInToday]); // onTokenFound intentionally omitted — accessed via ref above

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      data-testid="qr-scanner-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black">
        <span className="text-sm font-semibold text-white">Check In</span>
        <button onClick={onClose} className="p-1 text-white opacity-70 hover:opacity-100" data-testid="qr-scanner-close">
          <X size={20} />
        </button>
      </div>

      {alreadyCheckedInToday ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
            <Check size={32} className="text-green-400" />
          </div>
          <p className="text-white font-semibold text-lg mb-1">Already Checked In</p>
          <p className="text-gray-400 text-sm">You have already checked in today.</p>
          <button
            onClick={onClose}
            className="mt-6 px-6 py-2 bg-green-600 text-white text-sm font-semibold rounded-full"
            data-testid="qr-scanner-done-btn"
          >
            Done
          </button>
        </div>
      ) : (
        <>
          {/* Scanner area */}
          <div className="flex-1 flex flex-col items-center justify-center relative">
            {/* Camera feed */}
            <div
              id="qr-scanner-reader"
              style={{ width: '100%', maxWidth: 360, borderRadius: 16, overflow: 'hidden' }}
            />

            {!scanning && !error && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader size={28} className="text-white animate-spin" />
              </div>
            )}

            {error && (
              <div className="absolute bottom-20 left-4 right-4 p-3 bg-red-900/80 rounded-xl">
                <p className="text-xs text-red-200 text-center">{error}</p>
              </div>
            )}
          </div>

          {/* Instruction */}
          <div className="px-6 pb-10 pt-4 flex flex-col items-center gap-3 bg-black">
            <p className="text-sm text-gray-300 text-center">
              Scan the QR code to complete check-in
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const ActivityJourney = ({ activity, organizer, alreadyRegistered, isInterested = false, isExpired, onScrollToForm, onScrollToInterested, registrationType, disableCollapse = false, installCTA = null }) => {
  const { user, setUser } = useAuth();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  // Track which pending steps have been clicked open for preview.
  // When disableCollapse is true (my-journeys page), all steps start expanded.
  const [expandedSteps, setExpandedSteps] = useState(
    disableCollapse ? new Set([1, 2, 3, 4, 5]) : new Set()
  );
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [inspireLoading, setInspireLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shownCaption, setShownCaption] = useState(null); // { type: 'whatsapp'|'instagram', text: string }
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrCheckinLoading, setQrCheckinLoading] = useState(false);
  const [qrCheckinError, setQrCheckinError] = useState(null); // shown in step 3 after failure
  const qrCheckinInFlightRef = useRef(false); // prevents concurrent check-in calls
  const profilePicInputRef = useRef(null);
  const [profilePicUploading, setProfilePicUploading] = useState(false);

  const activityId = activity?.id;

  // ── No-registration / external-link flow (computed early for query gating) ────
  const isNoReg  = !registrationType;            // no registration at all
  const isExtReg = registrationType === 'link';  // external link — cannot verify, so treat like no-reg
  // For no-reg AND ext-reg activities, clicking "Interested" is sufficient to progress the journey
  const effectiveRegisteredEarly = (isNoReg || isExtReg) ? (isInterested || false) : alreadyRegistered;

  const toggleExpanded = (step) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(step)) next.delete(step); else next.add(step);
      return next;
    });
  };

  const { data: journeyProgress } = useQuery({
    queryKey: ['journey-progress', activityId],
    queryFn: async () => (await getJourneyProgress(activityId)).data,
    enabled: !!activityId && !!user,
    staleTime: 0,           // always fresh — ensures card type changes by org are picked up immediately
    refetchOnWindowFocus: true,
  });

  const { data: inviteData } = useQuery({
    queryKey: ['my-invites', activityId],
    queryFn: async () => (await getMyActivityInvites(activityId)).data,
    enabled: !!activityId && !!user && effectiveRegisteredEarly,
    staleTime: 60_000,
  });

  const inviteCount = inviteData?.count ?? 0;

  const { data: inviteCodeData } = useQuery({
    queryKey: ['invite-code', activityId],
    queryFn: async () => (await getInviteCode(activityId)).data,
    enabled: !!activityId && !!user && effectiveRegisteredEarly,
    staleTime: Infinity,
  });

  // Inspire image state — must be before any early return (Rules of Hooks)
  const [inspireImageUrl, setInspireImageUrl] = useState(
    activity?.inspire_image_url || journeyProgress?.inspire_image_url || DEFAULT_INSPIRE_IMAGE_URL
  );
  const [inspireGenerating, setInspireGenerating] = useState(false);

  // Track whether we already attempted generation this component session.
  // Using a ref (not state) so changing it never causes a re-render loop.
  const hasGeneratedRef = useRef(false);

  // Track the card type that was last used for generation so we can detect
  // when the org changes their "Inspire Others" selection mid-session.
  const lastCardTypeRef = useRef(null);

  // Sync inspire image: use admin-uploaded direct image immediately.
  // For per-user generated URLs: only restore from journeyProgress cache when the
  // session guard is active (hasGeneratedRef = true), meaning we already confirmed
  // the URL matches the current card type this session. This prevents stale cached
  // URLs from being shown after the org changes their Inspire Others selection.
  useEffect(() => {
    if (activity?.inspire_image_url) {
      setInspireImageUrl(activity.inspire_image_url);
    } else if (journeyProgress?.inspire_image_url && hasGeneratedRef.current) {
      setInspireImageUrl(journeyProgress.inspire_image_url);
    }
  }, [activity?.inspire_image_url, journeyProgress?.inspire_image_url]);

  // Proactively generate/refresh the card in the background as soon as Step 5 is
  // eligible.  The backend returns a cached URL instantly on fingerprint-match
  // (profile pic, event photo, etc. unchanged) — no render cost.
  // If the fingerprint changed (e.g. user updated profile pic, or org changed
  // the Inspire Others card type), a fresh card is rendered silently.
  useEffect(() => {
    if (!activity) return;
    const cardType = inspireCardType; // uses journeyProgress-priority value (already computed above)
    if (cardType === 'photo_from_organiser') {
      if (!activity.inspire_card_event_photo_url) return;
    } else if (cardType !== 'generic') {
      return;
    }
    if (activity.inspire_image_url) return;   // admin direct image — no generation needed
    if (inspireGenerating) return;
    if (journeyProgress?.attended !== true) return;

    // Detect card type change — org changed their Inspire Others selection.
    // Reset the session guard and clear the stale card so the correct new card is shown.
    const typeChanged = lastCardTypeRef.current !== null && lastCardTypeRef.current !== cardType;
    if (typeChanged) {
      hasGeneratedRef.current = false;
      setInspireImageUrl(null);
    }

    if (hasGeneratedRef.current) return;       // already confirmed/generated this session

    // Record the card type we're generating for, BEFORE the async call
    lastCardTypeRef.current = cardType;
    hasGeneratedRef.current = true;
    setInspireGenerating(true);
    generateInspireImage(activity.id || activity.slug)
      .then(({ data }) => {
        if (data?.inspire_image_url) setInspireImageUrl(data.inspire_image_url);
      })
      .catch(() => {
        hasGeneratedRef.current = false; // allow one retry on failure
      })
      .finally(() => setInspireGenerating(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activity?.id,
    activity?.inspire_card_event_photo_url,
    journeyProgress?.attended,
    journeyProgress?.experience_shared,
    journeyProgress?.inspire_card_type,  // re-run when org changes card type
    activity?.inspire_card_type,          // also watch activity-level type as fallback
  ]);

  if (!activity) return null;

  // Use the early computed values (same result)
  const effectiveRegistered = effectiveRegisteredEarly;

  // Per-activity inspire badge (admin-uploaded)
  const hasInspireImage = !!activity.inspire_image_url;

  // ── States ───────────────────────────────────────────────────────────────────
  const states = computeStepStates({ alreadyRegistered: effectiveRegistered, inviteCount, journeyProgress, activity });
  const { s1, s2, s3, s4, s5, line12, line23, line34, line45, step3Active, showContactInfo, showAttendanceQuestion, attendanceClickable } = states;
  const { verificationEnabled, verificationType, verified, s5LockedMessage } = states;

  // Inspire image URL: admin-upload (if available) > generated personalized > default placeholder
  const attended         = journeyProgress?.attended ?? null;
  const experienceShared = journeyProgress?.experience_shared ?? false;
  const inspireShared    = journeyProgress?.inspire_shared ?? false;

  // Inspire card type — journeyProgress is always freshly fetched from DB, so it takes priority
  // over the potentially-stale activity prop (which can lag if the org changed the type recently)
  const inspireCardType  = journeyProgress?.inspire_card_type || activity?.inspire_card_type || 'generic';
  const certificateUrl   = journeyProgress?.certificate_url || null;

  const eventDateStr    = formatDate(activity.date || activity.date_start);
  const contacts        = activity.activity_contacts || [];
  const baseUrl         = window.location.origin;
  const slug            = activity.slug || activity.id;
  const inviteCode      = inviteCodeData?.code;
  const shortInviteUrl  = inviteCode ? `${baseUrl}/i/${inviteCode}` : `${baseUrl}/activities/${slug}`;
  const whatsAppUrl     = buildWhatsAppUrl(activity, shortInviteUrl);

  // Invite buttons shown when step 2 active/done and registered
  const showInviteButtons = (s2 === 'current' || s2 === 'done') && effectiveRegistered;

  // Resolve contacts: activity-specific first, then org phone fallback
  const resolvedContacts = (() => {
    if (contacts.length > 0) return contacts;
    const orgContact = organizer?.contact;
    if (!orgContact) return [];
    const phones = orgContact.phones?.length > 0 ? orgContact.phones : (orgContact.phone ? [orgContact.phone] : []);
    return phones.map((ph) => ({ name: organizer.name || '', phone: ph }));
  })();

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleAttendance = async (didAttend) => {
    setAttendanceLoading(true);
    try {
      await markParticipation(activityId, didAttend);
      queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] });
    } catch { /* ignore */ } finally { setAttendanceLoading(false); }
  };

  // QR check-in: called after scanner extracts a token from the QR code
  const handleQRTokenFound = async (token) => {
    if (qrCheckinInFlightRef.current) return; // prevent concurrent calls
    qrCheckinInFlightRef.current = true;
    setShowQRScanner(false);
    setQrCheckinError(null);
    setQrCheckinLoading(true);
    try {
      // Get user location for offline activities
      let latitude = null;
      let longitude = null;
      const loc = activity?.location;
      if (loc && !loc.is_online) {
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000 })
          );
          latitude  = pos.coords.latitude;
          longitude = pos.coords.longitude;
        } catch { /* location unavailable — backend will reject for offline */ }
      }
      const result = await qrCheckin(activityId, { token, latitude, longitude });
      const status = result.data?.status;
      if (status === 'checked_in' || status === 'already_checked_in') {
        queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Check-in could not be completed. Try again.';
      setQrCheckinError(msg);
    } finally {
      setQrCheckinLoading(false);
      qrCheckinInFlightRef.current = false;
    }
  };

  // ── Caption builder ───────────────────────────────────────────────────────────
  const buildShareCaption = () => {
    const name = organizer?.name || activity.other_organizer_name || 'the organiser';
    return `Glad I could be part of this event organised by ${name}.\nYou can find similar events happening around you on Karmana too! 👇\nkarmana.in/activities`;
  };

  // ── Shared download helper ────────────────────────────────────────────────────
  // Background pre-generation (above useEffect) ensures inspireImageUrl is already
  // fresh by the time the user reads Step 5 and decides to act.
  // Download/Share use it directly — no blocking fingerprint-check call needed.
  // refreshAndGetUrl is kept as a safety-net only (e.g. old open tabs).
  const refreshAndGetUrl = async () => {
    if (activity?.inspire_image_url) return activity.inspire_image_url;
    try {
      const { data } = await generateInspireImage(activity.id || activity.slug);
      if (data?.inspire_image_url) {
        setInspireImageUrl(data.inspire_image_url);
        return data.inspire_image_url;
      }
    } catch { /* fall through */ }
    return inspireImageUrl;
  };

  const doInspireDownload = async (url = inspireImageUrl) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'karmana-inspire.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  // ── Generic share (Web Share API primary; download + caption fallback) ────────
  const handleShare = async () => {
    if (inspireLoading) return;
    setInspireLoading(true);
    const caption = buildShareCaption();
    // Use pre-generated URL (background generation already completed before user clicks)
    const url = inspireImageUrl;
    try {
      if (navigator.share && url) {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          const file = new File([blob], 'karmana-inspire.png', { type: blob.type || 'image/png' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], text: caption });
            await markInspireShared(activityId);
            queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] });
            return;
          }
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }
      await doInspireDownload(url);
      setShownCaption({ text: caption });
      try { await navigator.clipboard.writeText(caption); } catch { /* ignore */ }
      await markInspireShared(activityId);
      queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] });
    } catch { /* ignore */ } finally { setInspireLoading(false); }
  };

  const handleInspireDownload = async () => {
    if (inspireLoading) return;
    setInspireLoading(true);
    // Use pre-generated URL directly — background generation has already run.
    // Download button is disabled while inspireGenerating, so by the time
    // this fires the URL is guaranteed to be fresh.
    try {
      await doInspireDownload(inspireImageUrl);
      await markInspireShared(activityId);
      queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] });
    } catch { /* ignore */ } finally { setInspireLoading(false); }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shortInviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePicUploading(true);
    try {
      const res = await uploadUserImage(file);
      const url = res.data?.url || res.data?.file_url;
      if (url) {
        await updateProfile({ profile_pic: url });
        const meRes = await getMe();
        setUser(meRes.data);
        toast.success('Profile pic updated!');
      }
    } catch { toast.error('Upload failed. Try again.'); }
    finally { setProfilePicUploading(false); if (e.target) e.target.value = ''; }
  };

  // ── Invite buttons — vertical column, right-aligned (like Step 5) ────────────
  const lockedInviteToastMsg = (isNoReg || isExtReg) ? 'Mark as Interested to invite others' : 'Register to invite friends';

  const inviteCTAcol = (locked) => (
    <div className={`flex flex-col gap-1.5 items-end flex-shrink-0 ${locked ? 'opacity-50' : ''}`}>
      {locked ? (
        <button
          onClick={() => toast.info(lockedInviteToastMsg)}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-green-600 rounded-full cursor-not-allowed whitespace-nowrap min-w-[88px]"
          data-testid="journey-whatsapp-invite-locked">
          <WAIcon /> Invite
        </button>
      ) : (
        <a href={whatsAppUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-green-600 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap min-w-[88px]"
          data-testid="journey-whatsapp-invite-btn">
          <WAIcon /> Invite
        </a>
      )}
      {locked ? (
        <button
          onClick={() => toast.info(lockedInviteToastMsg)}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-full cursor-not-allowed whitespace-nowrap min-w-[88px]"
          data-testid="journey-copy-invite-locked">
          <Copy size={11} /> Copy link
        </button>
      ) : (
        <button onClick={handleCopyLink} title={copied ? 'Copied!' : 'Copy invite link'}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap min-w-[88px]"
          data-testid="journey-copy-invite-link">
          {copied ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
      )}
    </div>
  );

  // ── Step 2 content — text left, CTA column right (aligned with top of text) ──
  let step2Content = null;
  if (s2 !== 'pending') {
    step2Content = (
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {inviteCount > 0 ? (
            <p className="text-xs text-green-600 leading-snug" data-testid="journey-invite-count">
              {inviteCount} friend{inviteCount > 1 ? 's' : ''} invited
            </p>
          ) : (
            <p className="text-xs text-gray-400 leading-snug">Invite someone who may be interested in this</p>
          )}
        </div>
        {showInviteButtons && inviteCTAcol(false)}
      </div>
    );
  } else if (expandedSteps.has(2)) {
    step2Content = (
      <div className="flex items-start gap-3">
        <p className="text-xs text-gray-400 flex-1 min-w-0 leading-snug">Invite someone who may be interested in this</p>
        {inviteCTAcol(true)}
      </div>
    );
  }

  // ── Step 3: contact CTA (always shown when contacts exist) ──────────────────
  const participateContactRight = resolvedContacts.length > 0 ? (
    <div className="flex flex-col gap-1.5 items-end" data-testid="journey-contact-card">
      {resolvedContacts.map((c, idx) => (
        c.phone ? (
          <a key={idx} href={`tel:+91${c.phone}`}
            className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-green-50 text-green-700 border border-green-200 rounded-full hover:bg-green-100 transition-colors min-w-[88px] w-[88px]"
            data-testid={`journey-contact-call-${idx}`}>
            <Phone size={11} className="flex-shrink-0" />
            <span className="truncate">{c.name || 'Contact'}</span>
          </a>
        ) : null
      ))}
    </div>
  ) : null;

  // ── Step 3: body content ─────────────────────────────────────────────────────
  const lockedAttendanceMsg = !effectiveRegistered
    ? ((isNoReg || isExtReg) ? 'Mark as Interested to access' : 'Register to access')
    : 'Opens after the event is over';

  let step3Content = null;
  if (s3 !== 'pending') {
    if (showAttendanceQuestion) {
      step3Content = (
        <div data-testid="journey-attendance-question">
          <p className="text-xs text-gray-500 mb-1.5">Did you Participate?</p>
          {qrCheckinError && (
            <p className="text-xs text-red-500 mb-2" data-testid="journey-qr-checkin-error">
              {qrCheckinError}
            </p>
          )}
          <div className="flex gap-2">
            {attendanceClickable ? (
              <>
                {verificationEnabled && verificationType === 'qr_code' && attended !== true ? (
                  // QR mode + not yet attended: Yes opens scanner
                  <button
                    onClick={() => { setQrCheckinError(null); setShowQRScanner(true); }}
                    disabled={qrCheckinLoading}
                    className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border border-green-500 text-green-700 rounded-full hover:bg-green-50 transition-colors disabled:opacity-50"
                    data-testid="journey-attendance-yes-qr"
                  >
                    {qrCheckinLoading ? <Loader size={10} className="animate-spin" /> : <QrCode size={11} />}
                    {qrCheckinLoading ? 'Checking in…' : 'Yes'}
                  </button>
                ) : (
                  // Standard Yes (or QR already attended — stays highlighted, no re-scan needed)
                  <button
                    onClick={() => { if (attended !== true) handleAttendance(true); }}
                    disabled={attendanceLoading}
                    className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors disabled:opacity-50 ${
                      attended === true
                        ? 'bg-green-50 text-green-700 border border-green-500'
                        : 'border border-green-500 text-green-700 hover:bg-green-50'
                    }`}
                    data-testid="journey-attendance-yes"
                  >
                    Yes
                  </button>
                )}
                <button
                  onClick={() => { if (attended !== false) handleAttendance(false); }}
                  disabled={attendanceLoading || qrCheckinLoading}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors disabled:opacity-50 ${
                    attended === false
                      ? 'bg-gray-500 text-white border border-gray-500'
                      : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="journey-attendance-no"
                >
                  No
                </button>
              </>
            ) : (
              // Locked — event hasn't started yet
              <div className="flex gap-2 opacity-50">
                <button
                  onClick={() => toast.info('You can mark after the event is over')}
                  className="px-3 py-1 text-xs font-semibold border border-green-500 text-green-700 rounded-full cursor-not-allowed"
                  data-testid="journey-attendance-yes-prestart">Yes</button>
                <button
                  onClick={() => toast.info('You can mark after the event is over')}
                  className="px-3 py-1 text-xs font-semibold bg-gray-100 border border-gray-200 text-gray-600 rounded-full cursor-not-allowed"
                  data-testid="journey-attendance-no-prestart">No</button>
              </div>
            )}
          </div>
          {/* QR check-in success confirmation */}
          {verificationEnabled && verificationType === 'qr_code' && attended === true && verified && (
            <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1" data-testid="journey-qr-checkin-success">
              <Check size={11} />
              You have checked in.
            </p>
          )}
        </div>
      );
    }
  } else if (expandedSteps.has(3)) {
    // Step 3 pending — locked preview
    step3Content = (
      <div data-testid="journey-participate-locked">
        <p className="text-xs text-gray-500 mb-1.5">Did you Participate?</p>
        <div className="flex gap-2 opacity-50">
          <button onClick={() => toast.info(lockedAttendanceMsg)}
            className="px-3 py-1 text-xs font-semibold border border-green-500 text-green-700 rounded-full cursor-not-allowed"
            data-testid="journey-attendance-yes-locked">Yes</button>
          <button onClick={() => toast.info(lockedAttendanceMsg)}
            className="px-3 py-1 text-xs font-semibold bg-gray-100 border border-gray-200 text-gray-600 rounded-full cursor-not-allowed"
            data-testid="journey-attendance-no-locked">No</button>
        </div>
      </div>
    );
  }

  // ── Step 4 content ───────────────────────────────────────────────────────────
  const isVideoUrl = (url) => /\.(mp4|mov|webm|avi|wmv)(\?.*)?$/i.test(url || '');

  let step4Content = null;
  if (s4 !== 'pending') {
    const expImages = (journeyProgress?.experience_media_urls || []).filter(u => !isVideoUrl(u));
    const expVideos = (journeyProgress?.experience_media_urls || []).filter(u => isVideoUrl(u));

    step4Content = s4 === 'done' ? (
      /* Render the user's own post in the same CommentCard style used on the activity page */
      <div className="flex gap-2.5" data-testid="journey-experience-shared">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {user?.profile_pic ? (
            <img src={user.profile_pic} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-[10px] font-bold text-green-700 leading-none">
              {(user?.name || 'Me').slice(0, 2).toUpperCase()}
            </div>
          )}
        </div>

        {/* Comment bubble */}
        <div className="flex-1 min-w-0">
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-xs font-semibold text-gray-700 mb-1 leading-none">{user?.name || 'You'}</p>

            {journeyProgress?.experience_text && (
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-snug">{journeyProgress.experience_text}</p>
            )}

            {expImages.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {expImages.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`experience ${idx + 1}`}
                    className="h-16 w-16 object-cover rounded-lg"
                    data-testid={`journey-experience-image-${idx}`}
                  />
                ))}
              </div>
            )}

            {expVideos.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {expVideos.map((url, idx) => (
                  <video key={idx} src={url} className="h-16 rounded-lg object-cover max-w-[80px]" controls playsInline />
                ))}
              </div>
            )}

            {!journeyProgress?.experience_text && !journeyProgress?.experience_media_urls?.length && (
              <p className="text-xs text-gray-400">Experience shared!</p>
            )}
          </div>
        </div>
      </div>
    ) : (
      <ExperienceForm activityId={activityId}
        onPosted={() => queryClient.invalidateQueries({ queryKey: ['journey-progress', activityId] })} />
    );
  } else if (expandedSteps.has(4)) {
    // Locked preview — clicking anywhere fires toast
    step4Content = (
      <div
        className="relative cursor-pointer"
        onClick={() => toast.info('Only Participants can share')}
        data-testid="journey-experience-locked"
      >
        <div className="pointer-events-none opacity-40 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
          <textarea
            readOnly
            placeholder="Share what it was like to participate…"
            rows={2}
            className="w-full px-3 pt-2.5 bg-transparent text-xs text-gray-800 placeholder-gray-400 resize-none"
          />
          <div className="flex items-center justify-between px-3 pb-2.5">
            <ImageIcon size={14} className="text-gray-400" />
            <div className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full opacity-60">
              <Send size={10} /> Submit
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 5 content (always shown) ────────────────────────────────────────────
  const lockedInspireButtons = (msg) => (
    <div className="flex flex-col gap-1.5 items-end">
      <div className="opacity-50">
        <button onClick={() => toast.info(msg)}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-green-600 rounded-full cursor-not-allowed whitespace-nowrap min-w-[88px]">
          <Share2 size={11} /> Share
        </button>
      </div>
      <div className="opacity-50">
        <button onClick={() => toast.info(msg)}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-full cursor-not-allowed whitespace-nowrap min-w-[88px]">
          <Download size={11} /> Download
        </button>
      </div>
      {msg && (
        <p className="text-[10px] text-gray-400 text-right leading-tight mt-0.5" data-testid="journey-s5-locked-message">
          {msg}
        </p>
      )}
    </div>
  );

  // Step 5 actions — branch on inspire card type
  const photoFromOrgHasImage = inspireCardType === 'photo_from_organiser' &&
    (activity.inspire_image_url || activity.inspire_card_event_photo_url);

  const step5Actions = s5 !== 'pending' ? (
    (inspireCardType === 'photo_from_organiser' && !photoFromOrgHasImage) ? (
      <p className="text-xs text-gray-400" data-testid="journey-card-coming-soon">
        Your inspire card is being prepared by the organiser. Check back soon!
      </p>
    ) : inspireCardType === 'certificate' ? (
      certificateUrl ? (
        <button
          onClick={() => window.open(certificateUrl, '_blank')}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap w-full"
          data-testid="journey-certificate-download"
        >
          <Download size={11} /> Download Certificate
        </button>
      ) : (
        <p className="text-xs text-gray-400" data-testid="journey-certificate-pending">
          Your certificate will be added by the organiser.
        </p>
      )
    ) : (
      // Generic card OR photo_from_organiser with image available — share + download + caption
      <>
        <button onClick={handleShare} disabled={inspireLoading || inspireGenerating}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-green-600 rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap min-w-[88px]"
          data-testid={inspireCardType === 'photo_from_organiser' ? 'journey-photo-share' : 'journey-inspire-share'}>
          <Share2 size={11} /> Share
        </button>
        <button onClick={handleInspireDownload} disabled={inspireLoading || inspireGenerating}
          className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap min-w-[88px]"
          data-testid={inspireCardType === 'photo_from_organiser' ? 'journey-photo-download' : 'journey-inspire-download'}>
          <Download size={11} /> Download
        </button>
        {(inspireLoading || inspireGenerating) && (
          <p className="text-[9px] text-gray-400 text-center leading-tight" data-testid="journey-inspire-loading-msg">
            Preparing For Download. May take few seconds.
          </p>
        )}
        {shownCaption && (
          <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-100" data-testid="journey-inspire-caption">
            <div className="flex items-start gap-1.5">
              <p className="flex-1 text-[10px] text-gray-600 whitespace-pre-line leading-relaxed">{shownCaption.text}</p>
              <button
                onClick={async () => { try { await navigator.clipboard.writeText(shownCaption.text); toast.success('Copied!'); } catch { /* ignore */ } }}
                className="flex-shrink-0 p-0.5 text-gray-400 hover:text-green-600 transition-colors mt-0.5"
                title="Copy caption"
                data-testid="journey-inspire-caption-copy">
                <Copy size={11} />
              </button>
            </div>
            <p className="text-[9px] text-gray-400 mt-1">Caption auto-copied to clipboard</p>
          </div>
        )}
      </>
    )
  ) : s5LockedMessage ? (
    lockedInspireButtons(s5LockedMessage)
  ) : (
    lockedInspireButtons('Only Participants can share')
  );

  const step5Content = (
    <div className="mt-1 flex items-start gap-3">
      {/* Image area — shown for all card types in the pending placeholder state, and for
          generic / photo_from_organiser / certificate when active */}
      {(inspireCardType === 'generic' || inspireCardType === 'photo_from_organiser' || inspireCardType === 'certificate') && (
        <div style={{ height: 160, width: 'auto', maxWidth: '55%', flexShrink: 0, position: 'relative' }}>
          {s5 === 'pending' ? (
            /* Step 5 locked — show card-type-specific static placeholder */
            <img
              src={
                inspireCardType === 'certificate' ? CERT_ICON :
                inspireCardType === 'photo_from_organiser' ? PHOTO_PLACEHOLDER :
                GENERIC_PLACEHOLDER
              }
              alt="Inspire card preview"
              style={{ height: 160, width: 'auto', maxWidth: '100%', borderRadius: 12, opacity: 0.65 }}
              data-testid="journey-inspire-placeholder"
            />
          ) : inspireCardType === 'certificate' ? (
            /* Certificate type: always show the cert icon box */
            <div
              style={{ height: 160, width: 110, borderRadius: 12, background: '#fff9f0', border: '1.5px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              data-testid="journey-inspire-cert-icon"
            >
              <img src={CERT_ICON} alt="Certificate" style={{ width: 80, height: 80, objectFit: 'contain' }} />
            </div>
          ) : inspireGenerating ? (
            /* Step 5 active and generation in progress */
            <div style={{ height: 160, width: 110, borderRadius: 12, background: '#f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 8px' }}
              data-testid="journey-inspire-loading">
              <Loader size={20} className="animate-spin text-green-600" />
              <span style={{ fontSize: 9, color: '#6b7280', textAlign: 'center', lineHeight: 1.4 }}>
                Preparing For Download. May take few seconds.
              </span>
            </div>
          ) : inspireImageUrl ? (
            /* Generated image ready */
            <img
              src={inspireImageUrl}
              alt="I Was Part of This"
              style={{ height: 160, width: 'auto', maxWidth: '100%', borderRadius: 12 }}
              data-testid="journey-inspire-image"
              onError={() => { setInspireImageUrl(null); }}
            />
          ) : (
            /* Active but no image yet (not currently generating) — show appropriate placeholder */
            <img
              src={inspireCardType === 'photo_from_organiser' ? PHOTO_PLACEHOLDER : GENERIC_PLACEHOLDER}
              alt="Inspire card preview"
              style={{ height: 160, width: 'auto', maxWidth: '100%', borderRadius: 12, opacity: 0.65 }}
              data-testid="journey-inspire-placeholder-active"
            />
          )}
        </div>
      )}
      <div className={`flex flex-col gap-1.5 flex-1 min-w-0 items-end ${(inspireCardType !== 'generic' && !photoFromOrgHasImage && inspireCardType !== 'certificate') ? 'w-full' : ''}`} style={{ paddingTop: 4 }}>
        {step5Actions}
        {/* Profile pic missing — only show when step 5 is active and pic is absent */}
        {s5 !== 'pending' && !user?.profile_pic && (
          <div className="text-[10px] text-amber-700 leading-tight mt-1.5 text-right">
            Profile Pic missing.{' '}
            <button
              className="underline font-semibold hover:text-amber-800"
              onClick={() => profilePicInputRef.current?.click()}
              data-testid="journey-upload-profile-pic-cta"
            >
              {profilePicUploading ? 'Uploading…' : 'Upload it'}
            </button>
          </div>
        )}
        <input
          ref={profilePicInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleProfilePicUpload}
          data-testid="journey-profile-pic-input"
        />
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white" data-testid="activity-journey">

      {/* QR Scanner Modal — fullscreen overlay */}
      {showQRScanner && (
        <QRScannerModal
          onTokenFound={handleQRTokenFound}
          onClose={() => { setShowQRScanner(false); setQrCheckinError(null); }}
          alreadyCheckedInToday={false}
        />
      )}
      {/* Header — toggle only when collapsible; hidden entirely on /my-journeys (disableCollapse) */}
      {!disableCollapse && (
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4">
          <button onClick={() => setIsOpen((v) => !v)} className="flex items-center gap-1.5 flex-1 min-w-0" data-testid="journey-toggle">
            <img src="/images/karmana-journey-logo.png" alt="Karmana Journeys" className="w-5 h-5 rounded-full object-contain flex-shrink-0" />
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Activity Journey</span>
            {isOpen ? <ChevronUp size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />}
          </button>
          {installCTA && <div className="flex-shrink-0 ml-2">{installCTA}</div>}
        </div>
      )}

      {/* Steps */}
      {(disableCollapse || isOpen) && (
        <div className="px-4 pt-4 pb-5">

          {/* Step 1 — Register to Participate / Interested to Join */}
          <StepRow
            state={s1}
            primary
            title={(isNoReg || isExtReg)
              ? (effectiveRegistered ? "You're Interested to Join" : 'Interested to Join')
              : (effectiveRegistered ? 'Registration Done' : 'Register to Participate')
            }
            inlineMeta={!effectiveRegistered ? <YoureHereBadge /> : undefined}
            isFirst
            lineAfter={line12}
            onTitleClick={!effectiveRegistered
              ? ((isNoReg || isExtReg) ? onScrollToInterested : onScrollToForm)
              : undefined}
          />

          {/* Step 2 — Bring Your Friends */}
          <StepRow
            state={s2}
            primary={false}
            title="Bring Your Friends"
            lineAfter={line23}
            onTitleClick={s2 === 'pending' ? () => toggleExpanded(2) : undefined}
          >
            {step2Content}
          </StepRow>

          {/* Step 3 — Participate */}
          <StepRow
            state={s3}
            primary
            title="Participate"
            inlineMeta={eventDateStr ? <DateBadge date={eventDateStr} /> : undefined}
            lineAfter={line34}
            rightContent={participateContactRight}
            onTitleClick={s3 === 'pending' ? () => toggleExpanded(3) : undefined}
          >
            {step3Content}
          </StepRow>

          {/* Step 4 — Share Your Experience */}
          <StepRow
            state={s4}
            primary={false}
            noTick
            title="Share Your Experience"
            lineAfter={line45}
            onTitleClick={s4 === 'pending' ? () => toggleExpanded(4) : undefined}
          >
            {step4Content}
          </StepRow>

          {/* Step 5 — Inspire Others (image always visible) */}
          <StepRow
            state={s5}
            primary
            title="Inspire Others"
            isLast
            lineAfter={false}
          >
            {step5Content}
          </StepRow>

        </div>
      )}
    </div>
  );
};

export default ActivityJourney;
